import classNames from 'classnames';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import Box from 'ui-box';
import { majorScale } from '.';
import { Button, IButtonProps } from './Button';
import { DateInput } from './DateInput';
import { Menu, MenuDivider, MenuItem } from './Menu';
import { Popover } from './Popover';

export interface IDateFilterProps extends IButtonProps {
  onSelect?: (range: { from: Date; to?: Date } | null) => void;
  dateRange?: { from: Date; to?: Date };
}

const DateFilter: React.FC<IDateFilterProps> = ({ onSelect, ...props }) => {
  const intl = useIntl();
  const [dateRange, setDateRange] = useState<
    { from: Date; to?: Date } | undefined
  >(props.dateRange);
  const [lock, setLock] = useState(true);

  useEffect(() => {
    setDateRange(props.dateRange);
  }, [props.dateRange]);

  useEffect(() => {
    if (lock) return;
    setLock(true);
    onSelect && onSelect(dateRange || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const thisMonth = { from: moment().startOf('month').toDate(), to: undefined };
  const thisQuarter = {
    from: moment().startOf('quarter').toDate(),
    to: undefined,
  };
  const thisYear = { from: moment().startOf('year').toDate(), to: undefined };
  const lastMonth = {
    from: moment().subtract(1, 'month').startOf('month').toDate(),
    to: moment().subtract(1, 'month').endOf('month').toDate(),
  };
  const lastQuarter = {
    from: moment().subtract(1, 'quarter').startOf('quarter').toDate(),
    to: moment().subtract(1, 'quarter').endOf('quarter').toDate(),
  };
  const lastYear = {
    from: moment().subtract(1, 'year').startOf('year').toDate(),
    to: moment().subtract(1, 'year').endOf('year').toDate(),
  };

  const items = [
    {
      interval: undefined,
      title: intl.formatMessage({ defaultMessage: 'Anytime' }),
    },
    {
      interval: thisMonth,
      title: intl.formatMessage({ defaultMessage: 'This month' }),
    },
    {
      interval: thisQuarter,
      title: intl.formatMessage({ defaultMessage: 'This quarter' }),
    },
    {
      interval: thisYear,
      title: intl.formatMessage({ defaultMessage: 'This year' }),
    },
    {
      interval: lastMonth,
      title: intl.formatMessage({ defaultMessage: 'Last month' }),
    },
    {
      interval: lastQuarter,
      title: intl.formatMessage({ defaultMessage: 'Last quarter' }),
    },
    {
      interval: lastYear,
      title: intl.formatMessage({ defaultMessage: 'Last year' }),
    },
  ];

  const selectedItem = items.find((item) => {
    const isSameFrom =
      item.interval &&
      item.interval.from &&
      dateRange &&
      dateRange.from &&
      moment(item.interval.from).isSame(dateRange.from);
    const isSameTo =
      (item.interval &&
        item.interval.to &&
        dateRange &&
        dateRange.to &&
        moment(item.interval.to).isSame(dateRange.to)) ||
      (item.interval && !item.interval.to && dateRange && !dateRange.to);
    let selected = (!item.interval && !dateRange) || (isSameFrom && isSameTo);
    return selected;
  });

  console.log(selectedItem);

  return (
    <Popover
      placement="bottom-end"
      content={(close) => (
        <Menu>
          <MenuDivider text="Filtrer efter dato" />
          {items.map((item, i) => {
            let selected = selectedItem === item;
            return (
              <MenuItem
                key={i}
                text={
                  <span className={classNames(selected && 'medium')}>
                    {item.title}
                  </span>
                }
                icon={selected ? 'small-tick' : undefined}
                onClick={() => {
                  close();
                  setLock(false);
                  setDateRange(item.interval);
                }}
              />
            );
          })}
          <MenuDivider text="Anden dato" />
          <Box
            display="grid"
            gridTemplateColumns="1fr 1fr"
            columnGap={majorScale(1)}
            paddingX={majorScale(2)}
            paddingBottom={majorScale(1)}
          >
            <DateInput
              placement="bottom-end"
              value={dateRange?.from}
              onChange={(date) => {
                if (date) {
                  setLock(false);
                  setDateRange((dr) => ({
                    from: date,
                    to: dr?.to,
                  }));
                } else {
                  setLock(false);
                  setDateRange(undefined);
                }
              }}
            />
            <DateInput
              placement="bottom-end"
              value={dateRange?.to}
              onChange={(date) => {
                if (date) {
                  setLock(false);
                  setDateRange((dr) => ({
                    from: dr ? dr.from : date,
                    to: date,
                  }));
                } else if (date && dateRange?.from) {
                  setLock(false);
                  setDateRange((dr) => ({
                    from: dateRange.from,
                    to: undefined,
                  }));
                }
              }}
            />
          </Box>
        </Menu>
      )}
    >
      <Button iconLeft="calendar" iconRight="caret-down" {...props}>
        {dateRange
          ? selectedItem
            ? selectedItem.title
            : intl.formatMessage(
                { defaultMessage: '{from, date} to {to, date}' },
                { from: dateRange.from, to: dateRange.to }
              )
          : intl.formatMessage({ defaultMessage: 'Anytime' })}
      </Button>
    </Popover>
  );
};

export default DateFilter;
